// Philosophy types
export interface Philosophy {
  id: string
  name: string
  narrative?: string
  created_at: string
  updated_at: string
  theses_count?: number
}

export interface PhilosophyCreate {
  name: string
  narrative?: string
}

export interface PhilosophyUpdate {
  name?: string
  narrative?: string
}

// Thesis types
export type ThesisStatus = 'active' | 'paused' | 'archived'

export interface Thesis {
  id: string
  philosophy_id: string
  name: string
  narrative?: string
  target_allocation_pct?: number
  status: ThesisStatus
  created_at: string
  updated_at: string
  rules_count?: number
  holdings_count?: number
}

export interface ThesisCreate {
  philosophy_id: string
  name: string
  narrative?: string
  target_allocation_pct?: number
  status?: ThesisStatus
}

export interface ThesisUpdate {
  name?: string
  narrative?: string
  target_allocation_pct?: number
  status?: ThesisStatus
}

// Rule types
export type RuleAction = 'alert' | 'log' | 'email'
export type RulePriority = 'urgent' | 'high' | 'normal' | 'low'

export type ConditionType =
  | 'price_below'
  | 'price_above'
  | 'price_range'
  | 'drawdown'
  | 'ratio_above'
  | 'ratio_below'
  | 'crossover_above'
  | 'crossover_below'
  | 'concentration'
  | 'thesis_drift_above'
  | 'thesis_drift_below'
  | 'earnings_upcoming'
  | 'earnings_surprise'
  | 'earnings_streak'

// Condition config types for each condition type
export interface PriceConditionConfig {
  symbol: string
  value: number
}

export interface PriceRangeConditionConfig {
  symbol: string
  value_low: number
  value_high: number
}

export interface DrawdownConditionConfig {
  symbol: string
  threshold_pct: number
}

export interface RatioConditionConfig {
  numerator: string
  denominator: string
  threshold: number
}

export interface CrossoverConditionConfig {
  indicator: string
  threshold: number
}

export interface ConcentrationConditionConfig {
  threshold_pct: number
}

export interface ThesisDriftConditionConfig {
  thesis_id: string
  threshold_pct: number
}

export interface EarningsUpcomingConditionConfig {
  symbol: string
  days: number
}

export interface EarningsSurpriseConditionConfig {
  symbol: string
  direction: 'beat' | 'miss'
  threshold_pct: number
}

export interface EarningsStreakConditionConfig {
  symbol: string
  streak: number
}

export type ConditionConfig =
  | PriceConditionConfig
  | PriceRangeConditionConfig
  | DrawdownConditionConfig
  | RatioConditionConfig
  | CrossoverConditionConfig
  | ConcentrationConditionConfig
  | ThesisDriftConditionConfig
  | EarningsUpcomingConditionConfig
  | EarningsSurpriseConditionConfig
  | EarningsStreakConditionConfig
  | Record<string, unknown>

export interface Rule {
  id: string
  thesis_id: string
  thesis_name?: string
  name: string
  condition_type: ConditionType
  condition_config: ConditionConfig
  action: RuleAction
  priority: RulePriority
  cooldown_hours: number
  enabled: boolean
  last_triggered_at?: string
  created_at: string
  updated_at: string
}

export interface RuleCreate {
  thesis_id: string
  name: string
  condition_type: ConditionType
  condition_config: ConditionConfig
  action?: RuleAction
  priority?: RulePriority
  cooldown_hours?: number
  enabled?: boolean
}

export interface RuleUpdate {
  name?: string
  condition_type?: ConditionType
  condition_config?: ConditionConfig
  action?: RuleAction
  priority?: RulePriority
  cooldown_hours?: number
  enabled?: boolean
}

// AI Rule Suggestions
export interface RuleSuggestion {
  name: string
  condition_type: ConditionType
  condition_config: ConditionConfig
  priority: RulePriority
  rationale: string
}

export interface RuleSuggestionsResponse {
  thesis_id: string
  thesis_name: string
  suggestions: RuleSuggestion[]
  model: string
}

// Holding-Thesis linking
export interface HoldingThesisLink {
  thesis_ids: string[]
}

// Helper constants
export const CONDITION_TYPE_LABELS: Record<ConditionType, string> = {
  price_below: 'Price Below',
  price_above: 'Price Above',
  price_range: 'Price Range (Buy Zone)',
  drawdown: 'Drawdown',
  ratio_above: 'Ratio Above',
  ratio_below: 'Ratio Below',
  crossover_above: 'Crosses Above',
  crossover_below: 'Crosses Below',
  concentration: 'Position Concentration',
  thesis_drift_above: 'Thesis Over-Allocated',
  thesis_drift_below: 'Thesis Under-Allocated',
  earnings_upcoming: 'Earnings Upcoming',
  earnings_surprise: 'Earnings Surprise',
  earnings_streak: 'Earnings Streak',
}

export const PRIORITY_COLORS: Record<RulePriority, string> = {
  urgent: 'bg-red-100 text-red-800',
  high: 'bg-orange-100 text-orange-800',
  normal: 'bg-blue-100 text-blue-800',
  low: 'bg-gray-100 text-gray-800',
}

export const STATUS_COLORS: Record<ThesisStatus, string> = {
  active: 'bg-green-100 text-green-800',
  paused: 'bg-yellow-100 text-yellow-800',
  archived: 'bg-gray-100 text-gray-800',
}

// Thesis Allocation types
export type DriftStatus = 'ok' | 'warning' | 'alert'

export interface ThesisAllocation {
  thesis_id: string
  thesis_name: string
  target_allocation_pct: number | null
  current_allocation_pct: number
  drift_pct: number
  drift_status: DriftStatus
  holdings_count: number
  symbols: string[]
  total_cost_basis: number
  total_market_value: number
}

export interface ThesisAllocationsResponse {
  allocations: ThesisAllocation[]
  total_portfolio_value: number
  total_allocated_value: number
  unallocated_pct: number
}

export const DRIFT_STATUS_COLORS: Record<DriftStatus, string> = {
  ok: 'bg-green-100 text-green-800',
  warning: 'bg-yellow-100 text-yellow-800',
  alert: 'bg-red-100 text-red-800',
}

export const DRIFT_STATUS_LABELS: Record<DriftStatus, string> = {
  ok: 'On Target',
  warning: 'Drifting',
  alert: 'Off Target',
}

// Health Score types
export type HealthStatus = 'excellent' | 'good' | 'caution' | 'poor' | 'critical'

export interface ThesisHealthScore {
  thesis_id: string
  thesis_name: string
  health_score: number
  allocation_score: number
  alert_velocity_score: number
  performance_score: number
  drift_pct: number
  triggers_7d: number
  gain_loss_pct: number
  status: HealthStatus
}

export interface ThesisHealthScoresResponse {
  scores: ThesisHealthScore[]
  average_health: number
}

export const HEALTH_STATUS_COLORS: Record<HealthStatus, string> = {
  excellent: 'bg-green-100 text-green-800',
  good: 'bg-blue-100 text-blue-800',
  caution: 'bg-yellow-100 text-yellow-800',
  poor: 'bg-orange-100 text-orange-800',
  critical: 'bg-red-100 text-red-800',
}

export const HEALTH_SCORE_COLORS: Record<HealthStatus, string> = {
  excellent: 'text-green-600',
  good: 'text-blue-600',
  caution: 'text-yellow-600',
  poor: 'text-orange-600',
  critical: 'text-red-600',
}
